/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CampaignsView — list of SMS / voter messaging campaigns.
 *
 * Role wall: CAMPAIGN_MANAGER, MANAGER, ORG_MANAGER, ADMIN. Lower roles
 * see an access-denied card (defense in depth — backend enforces too).
 *
 * Layout:
 *   1. Role guard.
 *   2. Header + "New campaign" CTA.
 *   3. Filter row (status pill dropdown + free-text name search).
 *   4. Campaign table (server-listed; client-side name filter).
 *   5. Empty state with "Create your first campaign" CTA.
 *
 * Row click → onNavigate('campaign-detail', { campaignId }). If the host
 * App hasn't been wired yet, the view falls back to rendering
 * CampaignDetailView inline via a local selectedCampaignId.
 *
 * Backend list endpoint isn't all-the-way live yet (per api/messaging.ts
 * comments); a 404 is treated as "no campaigns yet" and gets the empty
 * state, not an error banner.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Megaphone, Plus, Search } from 'lucide-react';
import { listCampaigns } from '@/api/messaging';
import type { VoterCampaign, VoterCampaignStatus } from '@/types/messaging';
import EmptyState from '@/components/EmptyState';
import { ViewHeader } from '@/components/ViewHeader';
import { Skeleton } from '@/components/Skeleton';
import { CampaignDetailView } from './CampaignDetailView';
import { roleMatches } from '@/lib/roles';

// ---------------------------------------------------------------------------
// Role gate
// ---------------------------------------------------------------------------

const ALLOWED_ROLES = new Set([
  'SMS_MANAGER',
  'MANAGER',
  'ORG_MANAGER',
  'ADMIN',
]);

function isAllowed(userRole: string | undefined): boolean {
  if (!userRole) return false;
  return roleMatches(userRole, ALLOWED_ROLES);
}

// ---------------------------------------------------------------------------
// Status filter options
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sending', label: 'Sending' },
  { value: 'completed', label: 'Completed' },
  { value: 'paused', label: 'Paused' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatStatus(s: VoterCampaignStatus | string | undefined): {
  label: string;
  cls: string;
} {
  switch (s) {
    case 'draft':
      return {
        label: 'Draft',
        cls: 'bg-bg-elevated text-fg-secondary border-border-base',
      };
    case 'scheduled':
      return {
        label: 'Scheduled',
        cls: 'bg-status-warning-bg/30 text-status-warning border-status-warning-bg/50',
      };
    case 'sending':
      return {
        label: 'Sending',
        cls: 'bg-status-info-bg/30 text-status-info border-status-info-bg/50',
      };
    case 'completed':
      return {
        label: 'Completed',
        cls: 'bg-status-success-bg/30 text-status-success border-status-success-bg/50',
      };
    case 'paused':
      return {
        label: 'Paused',
        cls: 'bg-status-warning-bg/30 text-status-warning border-status-warning-bg/50',
      };
    case 'failed':
      return {
        label: 'Failed',
        cls: 'bg-status-danger-bg/30 text-status-danger border-status-danger-bg/50',
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        cls: 'bg-status-danger-bg/30 text-status-danger border-status-danger-bg/50',
      };
    default:
      return {
        label: String(s || '—'),
        cls: 'bg-bg-elevated text-fg-secondary border-border-base',
      };
  }
}

function formatMode(m: string): { label: string; cls: string } {
  if (m === 'batch') {
    return {
      label: 'Batch',
      cls: 'bg-status-info-bg/30 text-status-info border-status-info-bg/50',
    };
  }
  if (m === 'p2p') {
    return {
      label: 'P2P',
      cls: 'bg-accent/20 text-accent border-accent/40',
    };
  }
  return {
    label: m,
    cls: 'bg-bg-elevated text-fg-secondary border-border-base',
  };
}

function deliveryRate(c: VoterCampaign): string {
  if (!c.sent_count) return '—';
  const pct = Math.round((c.delivered_count / c.sent_count) * 100);
  return `${pct}%`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface CampaignsViewProps {
  userRole?: string;
  onNavigate?: (view: string, params?: Record<string, unknown>) => void;
}

export function CampaignsView({ userRole, onNavigate }: CampaignsViewProps) {
  // Role gate.
  if (!isAllowed(userRole)) {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">
            Access Denied
          </h2>
          <p className="text-fg-secondary">
            Campaigns are available to campaign managers and above. If you
            believe this is a mistake, contact your tenant admin.
          </p>
        </div>
      </div>
    );
  }

  const [campaigns, setCampaigns] = useState<VoterCampaign[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [nameSearch, setNameSearch] = useState<string>('');

  // Fallback inline navigation when the host App doesn't wire navigation yet.
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
    null,
  );

  // -------------------------------------------------------------------------
  // Fetch campaigns. Server-side status filter; client-side name search.
  // -------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listCampaigns({
      status: statusFilter || undefined,
    })
      .then((rows) => {
        if (cancelled) return;
        setCampaigns(rows);
        setLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 404) {
          // Endpoint not live yet — treat as no campaigns.
          setCampaigns([]);
          setLoading(false);
          return;
        }
        if (status === 403) {
          setError('Your role does not have access to campaigns.');
        } else if (status) {
          setError(`Backend error ${status} loading campaigns.`);
        } else {
          setError('Could not reach the messaging backend.');
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [statusFilter, reloadCounter]);

  // -------------------------------------------------------------------------
  // Client-side name filter
  // -------------------------------------------------------------------------
  const filtered = useMemo<VoterCampaign[]>(() => {
    if (!campaigns) return [];
    const q = nameSearch.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter((c) => c.name.toLowerCase().includes(q));
  }, [campaigns, nameSearch]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleNewCampaign = useCallback(() => {
    if (onNavigate) {
      onNavigate('campaign-composer');
    } else {
      // No navigator wired — best we can do is alert; integration will fix.
      // eslint-disable-next-line no-alert
      alert(
        'Composer navigation not wired yet. Please integrate onNavigate.',
      );
    }
  }, [onNavigate]);

  const handleOpenCampaign = useCallback(
    (id: number) => {
      if (onNavigate) {
        onNavigate('campaign-detail', { campaignId: id });
      } else {
        setSelectedCampaignId(id);
      }
    },
    [onNavigate],
  );

  const handleBackFromDetail = useCallback(() => {
    setSelectedCampaignId(null);
    setReloadCounter((n) => n + 1);
  }, []);

  // Inline-detail fallback — host hasn't wired onNavigate.
  if (selectedCampaignId !== null) {
    return (
      <CampaignDetailView
        campaignId={selectedCampaignId}
        userRole={userRole}
        onBack={handleBackFromDetail}
      />
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-accent" />
            Campaigns
          </span>
        }
        tagline="Batch SMS and P2P campaigns"
        actions={
          <button
            type="button"
            onClick={handleNewCampaign}
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent hover:bg-accent-hover text-white font-semibold text-sm px-3 py-2 transition-colors"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            New campaign
          </button>
        }
      />

      {/* Filter row */}
      <div className="bg-bg-raised rounded-xl p-4 border border-border-base">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
              Status
            </span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-bg-base border border-border-base rounded px-2 py-1.5 text-fg-primary text-sm min-w-[200px]"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 flex-1 min-w-[240px]">
            <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
              Name search
            </span>
            <div className="relative">
              <Search
                className="w-4 h-4 text-fg-muted absolute left-2.5 top-1/2 -translate-y-1/2"
                aria-hidden="true"
              />
              <input
                type="text"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                placeholder="Search by campaign name…"
                className="w-full bg-bg-base border border-border-base rounded pl-8 pr-3 py-1.5 text-fg-primary placeholder-fg-muted text-sm"
              />
            </div>
          </label>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={() => setReloadCounter((n) => n + 1)}
            className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && !error && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && campaigns && campaigns.length === 0 && (
        <EmptyState
          icon={Megaphone}
          title="No campaigns yet"
          description="You haven't created any SMS campaigns. Get started by composing your first one."
          action={{ label: 'Create your first campaign', onClick: handleNewCampaign }}
        />
      )}

      {/* Filtered-empty (have campaigns but name search excludes all) */}
      {!loading &&
        !error &&
        campaigns &&
        campaigns.length > 0 &&
        filtered.length === 0 && (
          <EmptyState
            icon={Search}
            title="No campaigns match your search"
            description="Try a different name or clear the filters."
            action={{
              label: 'Clear search',
              onClick: () => {
                setNameSearch('');
                setStatusFilter('');
              },
            }}
            compact
          />
        )}

      {/* Table */}
      {!loading && !error && filtered.length > 0 && (
        <div className="bg-bg-raised rounded-lg border border-border-base overflow-clip">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-base bg-bg-base">
                  <Th>Name</Th>
                  <Th>Mode</Th>
                  <Th>Status</Th>
                  <Th>Schedule</Th>
                  <Th align="right">Sent / Total</Th>
                  <Th align="right">Delivery rate</Th>
                  <Th>Created</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <CampaignRow
                    key={c.id}
                    campaign={c}
                    onOpen={() => handleOpenCampaign(c.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-fg-muted ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

function Pill({
  label,
  cls,
}: {
  label: string;
  cls: string;
}) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded border text-[11px] font-bold uppercase tracking-wide ${cls}`}
    >
      {label}
    </span>
  );
}

function CampaignRow({
  campaign,
  onOpen,
}: {
  campaign: VoterCampaign;
  onOpen: () => void;
}) {
  const statusPill = formatStatus(campaign.status);
  const modePill = formatMode(campaign.mode);
  // If "paused_at" is set, show Paused even if backend status is sending.
  const effectiveStatusPill =
    campaign.paused_at && campaign.status === 'sending'
      ? formatStatus('paused')
      : statusPill;
  return (
    <tr
      onClick={onOpen}
      className="border-b border-border-base hover:bg-bg-raised/60 cursor-pointer transition-colors"
    >
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="text-left group"
        >
          <span className="text-fg-primary font-medium group-hover:text-accent group-hover:underline">
            {campaign.name}
          </span>
        </button>
      </td>
      <td className="px-4 py-3">
        <Pill label={modePill.label} cls={modePill.cls} />
      </td>
      <td className="px-4 py-3">
        <Pill label={effectiveStatusPill.label} cls={effectiveStatusPill.cls} />
      </td>
      <td className="px-4 py-3 text-fg-secondary text-xs tabular-nums">
        {campaign.scheduled_at
          ? formatDateTime(campaign.scheduled_at)
          : campaign.started_at
            ? `Started ${formatDateTime(campaign.started_at)}`
            : 'Immediate'}
      </td>
      <td className="px-4 py-3 text-right text-fg-primary tabular-nums">
        {campaign.sent_count.toLocaleString()} /{' '}
        {campaign.total_recipients.toLocaleString()}
      </td>
      <td className="px-4 py-3 text-right text-fg-secondary tabular-nums">
        {deliveryRate(campaign)}
      </td>
      <td className="px-4 py-3 text-fg-muted text-xs tabular-nums">
        {formatDate(campaign.created_at)}
      </td>
    </tr>
  );
}

export default CampaignsView;
